import React from 'react';
import { X } from 'lucide-react';

interface TermsProps {
  isOpen: boolean;
  onClose: () => void;
}

function Terms({ isOpen, onClose }: TermsProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-start justify-center px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain touch-pan-y">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden my-auto">
        <div className="p-4 md:p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h1 className="text-2xl font-bold">Terms of Service</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close terms"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="overflow-y-auto p-4 md:p-6">
          <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <div className="prose prose-blue max-w-none">
            <p className="mb-6">
              Welcome to Angel Answer Engine. Please read these Terms of Service ("Terms") carefully before using our website operated by Terrez Wells ("us," "we," or "our").
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Use of the Service</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">1.1 Eligibility</h3>
            <p className="mb-4">
              You must be at least 13 years of age to use the Service. By using our Service, you represent that you are at least 13 years old.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">1.2 License to Use</h3>
            <p className="mb-4">
              Subject to your compliance with these Terms, we grant you a limited, non-transferable, non-exclusive, revocable license to use and access the Service for personal, non-commercial use.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">1.3 Prohibited Conduct</h3>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Use the Service for any illegal purpose or in violation of any local, state, national, or international law.</li>
              <li className="mb-2">Engage in any harassing, threatening, defamatory, misleading, or fraudulent behavior.</li>
              <li className="mb-2">Distribute viruses or other malicious code that may harm the Service or users of the Service.</li>
              <li className="mb-2">Use automated systems (e.g., robots, scrapers) to extract data from the Service without our prior permission.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Intellectual Property</h2>
            <p className="mb-6">
              All content included on the Service, such as text, graphics, logos, images, and software, is owned or licensed by us and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written consent.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. User-Generated Content</h2>
            <h3 className="text-lg font-medium mt-6 mb-3">3.1 Responsibility</h3>
            <p className="mb-4">
              If the Service allows you to post, upload, or otherwise submit content (e.g., questions, comments), you are solely responsible for the content that you submit.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.2 Rights Granted</h3>
            <p className="mb-4">
              By submitting content, you grant us a worldwide, non-exclusive, royalty-free, transferable license to use, reproduce, modify, publicly display, distribute, and otherwise exploit the content in connection with the Service.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.3 Content Restrictions</h3>
            <p className="mb-4">
              You agree not to submit content that is obscene, hateful, defamatory, harassing, infringing, or otherwise unlawful. We reserve the right to remove any content that violates these Terms or is otherwise objectionable.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Disclaimers</h2>
            <p className="mb-6">
              The Service is provided on an "as is" and "as available" basis. We disclaim all warranties, express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Service will be error-free, uninterrupted, or free from viruses or other harmful components.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Limitation of Liability</h2>
            <p className="mb-6">
              To the fullest extent permitted by law, in no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of data, revenue, or profit, arising out of or in connection with your use of the Service.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Indemnification</h2>
            <p className="mb-6">
              You agree to indemnify, defend, and hold harmless Terrez Wells and any affiliated parties from and against any claims, liabilities, damages, losses, and expenses (including legal and accounting fees) arising from your use of the Service or your violation of these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Termination</h2>
            <p className="mb-6">
              We reserve the right to suspend or terminate your access to the Service at any time, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, the licenses granted to you will terminate and you must cease using the Service.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Governing Law</h2>
            <p className="mb-6">
              These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Changes to These Terms</h2>
            <p className="mb-6">
              We may update or modify these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. By continuing to use the Service after any revisions become effective, you agree to be bound by the revised Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Contact Us</h2>
            <p className="mb-2">If you have any questions or concerns about these Terms, please contact:</p>
            <ul className="list-none mb-6">
              <li><strong>Name:</strong> Terrez Wells</li>
              <li><strong>Email:</strong> tezwells5@gmail.com</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Terms;